import React from "react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Box,
  Button,
  Field,
  FieldLabel,
  Input,
} from "@salestrak/ui";
import { useAuth } from "#app/lib/auth-context";
import { AuthRequestError } from "#app/lib/auth-service";
import { useAnalytics, AnalyticsContext, AuthEvent } from "#app/lib/analytics";
import { useRouter } from "#app/lib/hooks/router-compat";
import { GoogleSignInButton } from "./google-sign-in-button";

export function LoginForm() {
  const { signIn } = useAuth();
  const { track } = useAnalytics();
  const router = useRouter();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [googlePending, setGooglePending] = React.useState(false);
  const passwordRef = React.useRef<HTMLInputElement>(null);

  const handleLogin = async () => {
    if (submitting || googlePending) return;
    setError("");
    if (!email.trim() || !password.trim()) {
      setError("Email and password are required.");
      return;
    }
    setSubmitting(true);
    try {
      await signIn(email.trim(), password);
      track({ context: AnalyticsContext.Auth, event: AuthEvent.SignIn, label: "success" });
    } catch (err: any) {
      track({ context: AnalyticsContext.Auth, event: AuthEvent.SignIn, label: "failure" });
      if (err instanceof AuthRequestError && err.code === "EMAIL_NOT_VERIFIED") {
        // The server re-sent the verification link on this attempt, so the
        // check-your-email screen (with its resend affordance) is accurate.
        router.replace({
          pathname: "/(auth)/verify-email-sent",
          params: { email: email.trim() },
        });
        return;
      }
      setError(err.message ?? "Login failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // The form element owns submission, so Enter submits from either field.
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void handleLogin();
  };

  return (
    <Box as="form" direction="column" gap="xl" onSubmit={handleSubmit}>
      <GoogleSignInButton
        onError={setError}
        disabled={submitting}
        onPendingChange={setGooglePending}
      />

      <Box direction="column" gap="sm">
        <Field>
          <FieldLabel>Email</FieldLabel>
          <Input
            type="email"
            placeholder="name@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            autoComplete="email"
            disabled={submitting || googlePending}
          />
        </Field>

        <Field>
          <FieldLabel>Password</FieldLabel>
          <Input
            ref={passwordRef}
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            disabled={submitting || googlePending}
          />
        </Field>
      </Box>

      {error ? (
        <Alert variant="error">
          <AlertTitle>Unable to sign in</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Button
        type="submit"
        loading={submitting}
        disabled={!email.trim() || !password.trim() || googlePending}
      >
        Sign In
      </Button>

      <Box direction="row" justify="center">
        <Button
          variant="link"
          size="sm"
          onClick={() => router.push("/(auth)/forgot-password" as any)}
        >
          Forgot password?
        </Button>
      </Box>
    </Box>
  );
}
